import { Component } from '@theme/component';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #runtimeReady = false;
  #selectionGeneration = 0;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    this.#runtimeReady = false;
    this.removeAttribute('data-tfp-variant-picker-ready');
    super.connectedCallback();
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      // Archive sizes can start undecided and need a stable fieldset slot.
      if (initialCheckedIndex !== -1 || this.hasAttribute('data-archive-size-clearable')) {
        this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
      }
    });

    this.addEventListener('change', this.variantChanged);
    this.addEventListener('click', this.#clearArchiveSize);
    this.#resizeObserver.observe(this);
    this.#runtimeReady = true;
    this.setAttribute('data-tfp-variant-picker-ready', '');
  }

  updatedCallback() {
    this.removeAttribute('data-tfp-variant-picker-ready');
    super.updatedCallback();
    if (this.isConnected && this.#runtimeReady) this.setAttribute('data-tfp-variant-picker-ready', '');
  }

  disconnectedCallback() {
    this.#runtimeReady = false;
    this.removeAttribute('data-tfp-variant-picker-ready');
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('change', this.variantChanged);
    this.removeEventListener('click', this.#clearArchiveSize);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;
    const selectionGeneration = ++this.#selectionGeneration;

    this.updateSelectedOption(event.target);
    this.updateOptimisticProductPicker(selectedOption);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const productSectionId = this.dataset.sectionId;

    // Shopify reference pattern: combined-listing switches re-render only the
    // product section through the Section Rendering API, never the whole page.
    const morphElementSelector =
      loadsNewProduct && productSectionId ? `#${CSS.escape(`shopify-section-${productSectionId}`)}` : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    const localVariantState = this.getLocalVariantState(selectedOption, { isOnProductPage, loadsNewProduct });
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl, localVariantState },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        if (this.hasAttribute('data-archive-size-clearable') && selectionGeneration !== this.#selectionGeneration) return;
        const currentState = typeof history.state === 'object' && history.state !== null ? history.state : {};
        history.replaceState({ ...currentState }, '', url.toString());
      });
    }
  };

  #clearArchiveSize = (event) => {
    const input = event.target;
    if (!this.hasAttribute('data-archive-size-clearable') || !(input instanceof HTMLInputElement) ||
        input.type !== 'radio' || input.dataset.currentChecked !== 'true' || this.closest('product-card')) return;
    event.preventDefault();
    const generation = ++this.#selectionGeneration;
    // A later selection must win if input events run before this task.
    setTimeout(() => {
      if (!this.isConnected || generation !== this.#selectionGeneration) return;
      const request = new URL(this.buildRequestUrl(input));
      request.searchParams.delete('variant');
      request.searchParams.delete('option_values');
      for (const radio of this.querySelectorAll('input[type="radio"]')) {
        radio.checked = false;
        radio.dataset.currentChecked = 'false';
        radio.dataset.previousChecked = 'false';
      }
      this.#checkedIndices = this.#radios.map(() => []);
      const value = this.querySelector('.variant-option__selected-value');
      if (value) value.textContent = this.dataset.archiveSizePrompt || '';
      const inventory = this.querySelector('[data-variant-inventory-status]');
      if (inventory) { inventory.hidden = true; inventory.textContent = ''; inventory.dataset.inventoryState = ''; }
      const assistance = this.closest('.shopify-section')?.querySelector('tfp-product-oos-assistance');
      if (assistance && typeof assistance.applyVariant === 'function') assistance.applyVariant({ id: '', title: '', available: false });
      const url = new URL(location.href);
      url.searchParams.delete('variant');
      url.searchParams.delete('option_values');
      history.replaceState(history.state, '', url.href);
      this.fetchUpdatedSection(request.href);
      document.dispatchEvent(new CustomEvent('tfp:pdp-size-selected', { detail: { productId: this.dataset.productId, size: '' } }));
    }, 0);
  };

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    // Width measurements only drive the animated selection pill. Main PDP size
    // pickers intentionally render no pill, so avoid layout reads and CSS writes there.
    if (!fieldset.querySelector('.variant-option__button-label__pill')) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Gives the PDP picker immediate visual feedback while the canonical section
   * response reconciles price, media, availability, and product-form state.
   * @param {HTMLElement} selectedOption
   */
  updateOptimisticProductPicker(selectedOption) {
    if (this.dataset.templateProductMatch !== 'true' || selectedOption.closest('product-card')) return;

    const fieldset = selectedOption.closest('fieldset');
    const selectedValue = fieldset?.querySelector('.variant-option__selected-value');
    if (selectedValue) {
      selectedValue.textContent = selectedOption.dataset.displayValue || selectedOption.getAttribute('value') || '';
    }

    const inventoryStatus = this.querySelector('[data-variant-inventory-status]');
    if (!(inventoryStatus instanceof HTMLElement)) return;

    const inventoryKnown = selectedOption.dataset.inventoryKnown === 'true';
    const state = inventoryKnown ? selectedOption.dataset.inventoryState || '' : '';
    const message = inventoryKnown ? selectedOption.dataset.inventoryMessage || '' : '';

    inventoryStatus.classList.remove(
      'variant-option__inventory-status--last',
      'variant-option__inventory-status--few',
      'variant-option__inventory-status--low',
      'variant-option__inventory-status--out'
    );

    const stateClass = {
      last_item: 'variant-option__inventory-status--last',
      few_left: 'variant-option__inventory-status--few',
      low_stock: 'variant-option__inventory-status--low',
      out_of_stock: 'variant-option__inventory-status--out',
    }[state];

    if (stateClass) inventoryStatus.classList.add(stateClass);
    inventoryStatus.dataset.inventoryState = state;
    inventoryStatus.textContent = message;
    inventoryStatus.hidden = !message;
  }

  /**
   * Returns synchronous variant state only when this PDP selection maps directly
   * to one Shopify variant. Consumers may render this state immediately while
   * the existing Section Rendering request remains the canonical reconciliation.
   * @param {HTMLElement} selectedOption
   * @param {{ isOnProductPage: boolean, loadsNewProduct: boolean }} context
   */
  getLocalVariantState(selectedOption, { isOnProductPage, loadsNewProduct }) {
    if (
      !isOnProductPage ||
      loadsNewProduct ||
      this.querySelectorAll('fieldset, select').length !== 1
    ) {
      return undefined;
    }

    const variantId = selectedOption.dataset.variantId || '';
    const optionAvailable = selectedOption.dataset.optionAvailable;
    if (!variantId || (optionAvailable !== 'true' && optionAvailable !== 'false')) return undefined;

    return {
      variantId,
      available: optionAvailable === 'true',
      value: selectedOption.getAttribute('value') || '',
      displayValue: selectedOption.dataset.displayValue || selectedOption.getAttribute('value') || '',
      inventoryState: selectedOption.dataset.inventoryState || '',
      inventoryMessage: selectedOption.dataset.inventoryMessage || '',
    };
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // Preserve the latest product URL during fast combined-listing switches.
    const productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;

    const url = new URL(productUrl || window.location.pathname, window.location.origin);
    url.search = '';

    const viewParamValue = getViewParameterValue();
    if (viewParamValue) url.searchParams.set('view', viewParamValue);

    if (this.selectedOptionsValues.length && !source) {
      url.searchParams.set('option_values', this.selectedOptionsValues.join(','));
      if (this.dataset.marketSizeProduct === 'true' && selectedOption.dataset.variantId) {
        url.searchParams.set('variant', selectedOption.dataset.variantId);
      }
    } else if (source === 'product-card') {
      const optionValues = this.selectedOptionsValues.length
        ? sourceSelectedOptionsValues
        : [selectedOption.dataset.optionValueId].filter(Boolean);
      if (optionValues.length) url.searchParams.set('option_values', optionValues.join(','));
    }

    // Shopify Section Rendering API: request only the component/section that
    // actually needs to change instead of downloading the entire page HTML.
    let sectionId;
    if (this.closest('swatches-variant-picker-component')) {
      sectionId = 'section-rendering-product-card';
    } else if (this.dataset.templateProductMatch === 'true' && !this.closest('product-card')) {
      sectionId = this.dataset.sectionId;
    }

    if (sectionId) url.searchParams.set('section_id', sectionId);
    if (this.dataset.archivePreviewTheme) url.searchParams.set('preview_theme_id', this.dataset.archivePreviewTheme);

    return url.toString();
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string, localVariantState?: { variantId: string, available: boolean, value: string, displayValue: string, inventoryState: string, inventoryMessage: string } }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '', localVariantState } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();
    const sourceId = this.selectedOptionId;
    const currentProductId = this.dataset.productId ?? '';

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
          localVariantState,
        },
        promise: deferredEventPromise.promise,
      })
    );

    const activeController = this.#abortController;
    const responsePromise = fetch(requestUrl, { signal: activeController.signal }).then((response) => {
      if (!response.ok) {
        throw new Error(`Variant section render failed: ${response.status} ${response.statusText}`);
      }
      return response.text();
    });

    responsePromise
      .then(async (responseText) => {
        if (activeController.signal.aborted) {
          throw new DOMException('Variant request superseded', 'AbortError');
        }
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const responseVariantPicker = html.querySelector('variant-picker');
        const responseProductId =
          responseVariantPicker instanceof HTMLElement ? responseVariantPicker.dataset.productId ?? currentProductId : currentProductId;
        const responseProductUrl =
          responseVariantPicker instanceof HTMLElement ? responseVariantPicker.dataset.productUrl : undefined;
        const variantPickerJsonScript = responseVariantPicker?.querySelector('script[type="application/json"]');
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: responseProductId,
              sourceId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;
        if (responseProductId && responseProductUrl && responseProductId !== currentProductId) {
          newProduct = { id: responseProductId, url: responseProductUrl };
        }

        if (morphElementSelector) {
          await this.updateElement(html, morphElementSelector);
        } else if (!localVariantState) {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = (await this.updateVariantPicker(html)) ?? newProduct;

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }
        // Direct one-option PDP selections already updated the picker synchronously.
        // Section Rendering still reconciles downstream consumers, but the picker itself
        // should not be morphed a second time for the same state.

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: responseProductId,
                newProduct,
                sourceId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: responseProductId,
            newProduct,
            sourceId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        // Superseded variant requests are normal interaction flow, not errors.
        if (error?.name !== 'AbortError') console.error(error);
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  async updateVariantPicker(newHtml) {
    const { morph, MORPH_OPTIONS } = await import('@theme/morph');
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  async updateElement(newHtml, elementSelector) {
    const { morph } = await import('@theme/morph');
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }


  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const select = this.querySelector('select');
    if (select instanceof HTMLSelectElement) {
      const selected = select.selectedOptions[0];
      return selected instanceof HTMLOptionElement ? selected : undefined;
    }

    const selectedOption = this.querySelector('fieldset input:checked');
    return selectedOption instanceof HTMLInputElement ? selectedOption : undefined;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    const values = [];

    for (const group of this.querySelectorAll('fieldset, select')) {
      const selected =
        group instanceof HTMLSelectElement ? group.selectedOptions[0] : group.querySelector('input:checked');
      const optionValueId = selected?.dataset?.optionValueId;

      if (!optionValueId) throw new Error('No option value ID found');
      values.push(optionValueId);
    }

    return values;
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
